import React, { useState, useEffect, useRef } from 'react';
import {
  Home, BookOpen, Users, FileText, Coins, BarChart3,
  Package, ShoppingCart, Boxes, Wallet, Receipt, FolderKanban, Settings,
  Printer, ListChecks, PackageCheck,
} from 'lucide-react';
import { auth } from './auth';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from './db';
import { useFirestore } from './useFirestore';
import { useReceivings } from './useReceivings';   // V158 입고
import { useEffectiveOwner, makeEffectivePermissionChecker } from './hooks/useEffectiveOwner';
import StoreSwitcher from './components/StoreSwitcher';
import { findPriceByName, findPriceIndexByName, sameIngredientName } from './utils/ingredientMatch';   // V153 sameIngredientName
import { renameSemiEverywhere, syncSemiPrice } from './utils/semiRename';   // V141 반제품 이름 바꾸기 연동
import { insertIndexForCopy } from './utils/recipeCopy';   // V150 레시피 복사
import { fixSemiClash } from './utils/semiClash';   // V153 반제품·식재료 이름 겹침
import { orderKeyOf, findOrderRow } from './utils/orderKey';   // V142 발주 수량은 단가표 줄마다
import { getTermsFromMode, getIndustryMode, MODES } from './industryConfig';
import { useIsMobile } from './useIsMobile';
import { usePlan, PLANS } from './usePlan';
import Landing from './components/Landing';
import Sidebar from './components/Sidebar';
import RecipeView from './components/RecipeView';
import RecipeEditor from './components/RecipeEditor';
import OrderSheet from './components/OrderSheet';
import PurchaseOrder from './components/PurchaseOrder';
import PriceManager from './components/PriceManager';
import ManualPage from './components/ManualPage';
import AdminDashboard, { isAdmin } from './components/AdminDashboard';
import SubscriptionPage from './components/SubscriptionPage';
import PaymentResult from './components/PaymentResult';
import ProfitLoss from './components/ProfitLoss';
import OperationsTab from './components/OperationsTab';
import PurchaseLogPage from './components/PurchaseLogPage';
import ReceivingPage from './components/ReceivingPage';   // V158 입고
import GroupBuyingPage from './components/GroupBuyingPage';
import SettingsPage from './components/SettingsPage';
import TaxCenter from './components/TaxCenter';
import InventoryManager from './components/InventoryManager';
import PayrollManager from './components/PayrollManager';
import HomeView from './components/StaffHome';   // V147 중국어 화면 홈 = 직원용 工作台
import WorkToday from './components/WorkToday';   // V149 오늘의 작업
import PwaInstallPrompt from './components/PwaInstallPrompt';
import OnboardingTour, { isOnboardingDone, resetOnboarding } from './components/OnboardingTour';
import TutorialModal, { hasTutorial, isFirstVisit, markTutorialSeen, resetAllTutorials } from './components/TutorialModal';
import GuidedTour, { SEMFIT_TOUR } from './components/GuidedTour';
import { resetAllHints } from './components/Hint';
import EasterEggGame from './components/EasterEggGame';
import { clearKeyCache } from './crypto';
import SaveIndicator, { UnsavedBanner } from './components/SaveIndicator';   // V121 저장 상태
import ProjectManager from './components/ProjectManager';
import ShareView from './components/ShareView';
import { calcAvgUsage, mergeUsage, countNeedOrder } from './utils/inventoryAlerts';
import { calcRecipeBasedUsage } from './utils/recipeUsage';
import { LangProvider, LangToggle, T, LangText, storedLang } from './i18n/LangContext';   // V147 LangText·storedLang
import { trLang } from './i18n/L';   // V160 화면 전체 중국어
import { IS_CHINA_MODE } from './chinaMode';   // V148 연결 진단 링크
import { useStoreCountry } from './storeLocale';   // V135 매장 국가 (발주 묶음)
import { orderPackInfo, needToOrderQty } from './utils/orderPack';   // V135 수량 1 = 규격만큼
import { fillRecipeZh, translateMany, fillPriceZh, PRICE_ZH_FIELDS } from './utils/translate';
import { semiZhPatch, missingIngredientZh, mergePriceZh, listMissingRecipeZh } from './utils/recipeZh';   // V122 레시피 식재료 중국어
import { todayKey } from './utils/workCalc';
import { archiveDone, needsRollover } from './utils/workRollover';   // V156 다 한 작업은 내역, 안 한 작업은 다음 날

function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [copyingFrom, setCopyingFrom] = useState(null);   // V150 복사해서 새 버전 만들 레시피
  const [currentView, setCurrentView] = useState(() => {
    // PWA shortcut: ?tab=prices 등 URL 파라미터로 진입 시 해당 탭 열기
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const t = params.get('tab');
      const valid = ['home', 'recipes', 'orders', 'purchase', 'prices', 'profit', 'inventory', 'payroll', 'tax', 'manual'];
      if (t && valid.includes(t)) return t;
    }
    return 'home';
  });
  const [orderQty, setOrderQty] = useState({});
  const [orderNeed, setOrderNeed] = useState({});
  const [selectedServings, setSelectedServings] = useState({});
  const [showSubscription, setShowSubscription] = useState(false);
  const [showEasterEgg,    setShowEasterEgg]    = useState(false);
  const [logoClickCount,   setLogoClickCount]   = useState(0);
  const [modeDropdownOpen, setModeDropdownOpen] = useState(false);
  const [showOnboarding,   setShowOnboarding]   = useState(false);
  const [tabTutorial,      setTabTutorial]      = useState(null);  // 탭 ID — 자동 표시되는 TutorialModal
  const [helpMenuOpen,     setHelpMenuOpen]     = useState(false);
  const [showGuidedTour,   setShowGuidedTour]   = useState(false);
  // 현재 모드 — localStorage 우선, 없으면 가입 업종에서 자동 결정
  const [currentMode, setCurrentMode] = useState(
    () => localStorage.getItem('ddabak_mode') || ''
  );

  const isMobile = useIsMobile();
  // 모바일 레시피 탭: false=사이드바, true=레시피 상세
  const [showRecipeDetail, setShowRecipeDetail] = useState(false);

  // V50: 매장 전환 훅 — owner 모드 vs 매니저 모드
  const {
    effectiveOwnerUid, isManagerMode, currentStore,
    effectivePermissions, managedStores, switchStore,
    loading: ownerLoading,
  } = useEffectiveOwner(user);
  const hasPermission = makeEffectivePermissionChecker(effectivePermissions);

  const { categories, prices, orderHistory, profitLoss, taxData, inventory, industry, loading: dataLoading,
          loadedMode, switchMode,
          updateCategories, updatePrices, saveOrderHistory, deleteOrderHistory, mergeOrderHistory,   // V137 합치기
          updateOrderHistory,   // V138 수량 고치기
          updateOrderHistoryMany,   // V158 입고 → 발주서 상태
          updateProfitLoss, updateTaxData, updateInventory,
          projects, saveProject, deleteProject,
          employees, schedules, payrollSettings,
          updateEmployees, updateSchedules, updatePayrollSettings,
          menuRecipeMap, updateMenuRecipeMap,
          // V149 오늘의 작업
          workToday, workChecks, workHistory, updateWorkToday, setWorkCheck, saveWorkHistory, deleteWorkHistory,
          meetings, commitWork,   // V156 회의 · 넘기기
          // V121: 저장 보장 — 느린/실패한 불러오기, 저장 상태, 못 보낸 내용 복원
          slowLoad, loadError: dataLoadError, reload: reloadData, loadRetry, loadRetryMax,   // V148 자동 재시도
          saveState, retrySaves, unsavedBackup, restoreUnsaved, discardUnsaved } = useFirestore(user, effectiveOwnerUid);
  // V135: App은 LangProvider 바깥이라 매장 국가를 직접 구독한다 (같은 문서라 Firestore가 구독을 공유)
  const { country: storeCountry } = useStoreCountry(effectiveOwnerUid);
  // V160: 한/中을 바꾸면 화면 전체를 새 언어로 다시 그린다 (LangProvider가 알려 줌)
  const [uiLang, setUiLang] = useState(trLang());
  useEffect(() => {
    // 언어나 매장 통화(₩/¥)가 바뀌면 화면을 새로 그린다
    const on = (e) => { const d = e.detail || {}; setUiLang(`${d.lang === 'zh' ? 'zh' : 'ko'}-${d.country || ''}`); };
    window.addEventListener('semfit:lang', on);
    return () => window.removeEventListener('semfit:lang', on);
  }, []);
  // V158 입고 기록 (영수증 한 장 = 문서 하나, 사진은 따로)
  const { receivings, loading: receivingsLoading, save: saveReceiving, patch: patchReceiving, remove: removeReceiving,
          savePhoto: saveReceivingPhoto, loadPhoto: loadReceivingPhoto, removePhoto: removeReceivingPhoto } = useReceivings(user, effectiveOwnerUid);
  const { plan, canAccess, isLifetime, isApproved, trialActive, trialExpired, trialDaysLeft, loading: planLoading } = usePlan(user);

  // V109: 레시피 중국어는 저장 뒤 백그라운드로 채운다. 번역이 끝나는 몇 초 사이에
  // 다른 레시피가 저장될 수 있으므로, 패치는 항상 '그 시점의 최신' 목록에 건다.
  // (클로저에 잡힌 옛 categories로 덮어쓰면 그 사이 저장한 레시피가 사라진다)
  // ※ 아래 로딩/랜딩 early return보다 위에 있어야 한다 — 훅 순서 규칙
  // V111: effect가 아니라 렌더 중에 바로 갱신한다. 자식(Sidebar)의 effect가 부모 effect보다
  // 먼저 돌기 때문에, effect로 갱신하면 레시피 탭 자동 번역이 '빈 목록'을 보고 그냥 끝나버린다.
  const categoriesRef = useRef(categories);
  categoriesRef.current = categories;
  // V122: 번역이 끝난 뒤(비동기) 단가표에 중국어를 채울 때 '지금' 단가표에 건다
  const pricesRef = useRef(prices);
  pricesRef.current = prices;

  // 유효 모드 계산: 명시적 선택 → 가입 업종 자동 결정 → 기본값 food
  const effectiveMode = currentMode || getIndustryMode(industry) || 'food';
  const terms = getTermsFromMode(effectiveMode);
  const currentModeInfo = MODES.find(m => m.id === effectiveMode) || MODES[0];

  const handleModeChange = (modeId) => {
    setCurrentMode(modeId);
    localStorage.setItem('ddabak_mode', modeId);
    setModeDropdownOpen(false);
    // 데이터 즉시 전환
    if (user) switchMode(modeId);
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u);
      setAuthLoading(false);
      // 로그인 시 Firestore users 문서 자동 복구 (silent fail로 누락된 경우 대비)
      if (u) {
        try {
          const ref = doc(db, 'users', u.uid);
          const snap = await getDoc(ref);
          if (!snap.exists()) {
            // Auth만 있고 Firestore 문서 없으면 → 최소 프로필 자동 생성
            await setDoc(ref, {
              email: u.email || '',
              name: u.displayName || '',
              createdAt: new Date().toISOString(),
              autoRecovered: true,  // 자동 복구 표시 (admin이 식별용)
            }, { merge: true });
          }
        } catch (e) {
          console.warn('users 자동 복구 실패:', e);
        }
      }
    });
    return unsub;
  }, []);

  // effectiveMode가 실제 로드된 모드와 다르면 전환 (예: industry 로드 후 변경)
  useEffect(() => {
    if (user && !dataLoading && loadedMode && effectiveMode && effectiveMode !== loadedMode) {
      switchMode(effectiveMode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMode, loadedMode, dataLoading, user]);

  // V156: 날이 바뀌면 — 다 한 작업은 내역으로, 안 한 작업은 오늘 목록에 그대로 (어느 화면에 있든)
  const [dayKey, setDayKey] = useState(() => todayKey());
  useEffect(() => {
    const tick = () => setDayKey(todayKey());
    const id = setInterval(tick, 60 * 1000);
    document.addEventListener('visibilitychange', tick);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', tick); };
  }, []);
  useEffect(() => {
    if (!user || dataLoading || typeof commitWork !== 'function') return;
    if (!needsRollover(workToday, dayKey)) return;
    const r = archiveDone({ workToday, workChecks: workChecks || {}, workHistory: workHistory || [], today: dayKey, auto: true });
    if (r) commitWork({ workToday: r.workToday, workChecks: r.workChecks, workHistory: r.workHistory });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dataLoading, dayKey, workToday?.date, workToday?.items?.length]);

  // 첫 가입자 자동 OnboardingTour
  useEffect(() => {
    if (!user || dataLoading) return;
    if (!isOnboardingDone()) {
      // 1.5초 후 표시 (대시보드 로딩 후 자연스럽게)
      const t = setTimeout(() => setShowOnboarding(true), 1500);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dataLoading]);

  // 탭 변경 시 첫 방문이면 TutorialModal 자동 표시 (onboarding 끝난 후만)
  useEffect(() => {
    if (!user || dataLoading) return;
    if (showOnboarding) return;  // onboarding 중에는 탭 튜토리얼 띄우지 않음
    if (!isOnboardingDone()) return;  // onboarding 완료 전에는 안 띄움
    if (hasTutorial(currentView) && isFirstVisit(currentView)) {
      // 짧은 딜레이로 부드럽게
      const t = setTimeout(() => setTabTutorial(currentView), 400);
      return () => clearTimeout(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, user, dataLoading, showOnboarding]);

  // V147: 직원 계정 — 처음 여는 기기에서 '내 매장'(비어 있는 체험 매장) 대신 매니저로 들어간 매장으로 연다.
  //   중국 직원이 가입·로그인하면 빈 매장 + 한국어 체험 안내부터 보던 문제. 직접 고른 적이 있으면 그대로 둔다.
  useEffect(() => {
    if (!user || ownerLoading || planLoading) return;
    if (!managedStores || managedStores.length === 0 || isManagerMode) return;
    let stored = null;
    try { stored = localStorage.getItem('semfit_active_store_owner_uid'); } catch (e) { /* 무시 */ }
    if (stored) return;
    if (isApproved || isLifetime) return;                       // 이용권이 있는 사장님 계정은 내 매장 그대로
    if (!(trialActive || trialExpired || !plan || plan === 'free')) return;
    switchStore(managedStores[0].ownerUid);
  }, [user, ownerLoading, planLoading, managedStores, isManagerMode, isApproved, isLifetime, trialActive, trialExpired, plan, switchStore]);

  // V50/V52: 매니저 모드 권한 가드 — early return 모두보다 위에 있어야 Hook 순서 일관 (React #310)
  useEffect(() => {
    if (!isManagerMode) return;
    const TAB_CAT = {
      home: null, recipes: 'recipes', work: 'recipes', orders: 'transactions',
      purchase: 'orderHistory', prices: 'priceList', profit: 'operations',
      purchaseLog: 'purchaseLog', inventory: 'inventory',
      payroll: 'payroll', tax: 'tax', settings: null,
    };
    if (currentView === 'groupBuying') { setCurrentView('home'); return; }
    const cat = TAB_CAT[currentView];
    if (cat && !hasPermission('read', cat)) setCurrentView('home');
    // eslint-disable-next-line
  }, [isManagerMode, currentView, effectivePermissions]);


  // V148: 불러오기 실패 화면 — 20초마다 저절로 다시 시도 (중국 망은 몇 번 하면 붙는 경우가 많았다)
  const [retryIn, setRetryIn] = useState(0);
  useEffect(() => {
    if (!user || !dataLoadError) { setRetryIn(0); return undefined; }
    let left = 20;
    setRetryIn(left);
    const t = setInterval(() => {
      left -= 1;
      if (left <= 0) { clearInterval(t); setRetryIn(0); reloadData(); }
      else setRetryIn(left);
    }, 1000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, dataLoadError]);

  const zh0 = storedLang() === 'zh';   // V147 LangProvider 바깥 화면(로딩·체험 종료)은 기기에 저장된 언어로
  const L0 = (ko, zh) => (zh0 ? zh : ko);
  if (authLoading || (user && dataLoading)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', flexDirection: 'column', gap: '14px',
                    color: '#18181B', fontSize: '1rem', background: '#fff' }}>
        <img src="/semfit_symbol_t.png" alt=""
             style={{ width: 56, height: 56, objectFit: 'contain' }} />
        {user && dataLoadError ? (
          // V121: 불러오기 실패 — 예시 데이터를 띄우지 않는다(띄운 채 저장하면 진짜 데이터를 덮어쓴다)
          <>
            <p style={{ fontWeight: 800, color: '#B91C1C', fontSize: 15, margin: 0 }}>
              {L0('데이터를 불러오지 못했어요', '数据加载失败')}
            </p>
            <p style={{ color: '#71717A', fontSize: 12.5, margin: 0, textAlign: 'center', lineHeight: 1.6, padding: '0 24px' }}>
              {L0('인터넷 연결을 확인하고 다시 시도해 주세요. 불러오기 전에는 아무것도 저장되지 않아요.', '请检查网络后重试。加载完成前不会保存任何内容。')}
            </p>
            {retryIn > 0 && (
              <p data-load-autoretry style={{ color: '#A1A1AA', fontSize: 12, margin: 0 }}>
                {L0(`${retryIn}초 뒤에 저절로 다시 시도해요`, `${retryIn} 秒后自动重试`)}
              </p>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button onClick={reloadData}
                style={{ padding: '9px 18px', borderRadius: 10, border: 'none', background: '#18181B', color: '#fff', fontWeight: 800, fontSize: 13, cursor: 'pointer' }}>
                {L0('다시 시도', '重试')}
              </button>
              <button onClick={() => { clearKeyCache(); signOut(auth); }}
                style={{ padding: '9px 18px', borderRadius: 10, border: '1px solid #D4D4D8', background: '#fff', color: '#52525B', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
                {L0('로그아웃', '退出登录')}
              </button>
            </div>
            {/* V148: 어디서 막히는지 알 수 있게 — 오류 코드 + 중국 모드는 연결 진단 페이지 */}
            <p data-load-errcode style={{ color: '#D4D4D8', fontSize: 11, margin: 0 }}>{L0('오류 코드', '错误代码')}: {dataLoadError}</p>
            {IS_CHINA_MODE && (
              <a href="/diag.html" data-load-diag
                style={{ fontSize: 12.5, color: '#1A56A0', fontWeight: 700, textDecoration: 'underline' }}>
                {L0('🩺 연결 진단 열기 (결과를 사장님께 보내 주세요)', '🩺 打开连接诊断（把结果发给老板）')}
              </a>
            )}
          </>
        ) : (
          <>
            <p style={{ fontWeight: 700, color: '#52525B', fontSize: 14, margin: 0 }}>
              {L0('셈핏 로딩 중...', 'SEMFIT 加载中...')}
            </p>
            {user && slowLoad && (
              <p style={{ color: '#A1A1AA', fontSize: 12, margin: 0, textAlign: 'center', lineHeight: 1.6 }}>
                {L0('연결이 느려요. 조금만 기다려 주세요…', '网络较慢，请稍候…')}
                {loadRetry > 0 && <><br />{L0(`다시 연결하는 중 (${loadRetry}/${loadRetryMax})`, `正在重新连接（${loadRetry}/${loadRetryMax}）`)}</>}
              </p>
            )}
          </>
        )}
      </div>
    );
  }

  // V144: 로그인·회원가입 화면도 한/中 — 로그인 전에는 기기에 저장된 언어(없으면 휴대폰 언어)
  if (!user) return <LangProvider user={null} ownerUid={null}><React.Fragment key={uiLang}><Landing /></React.Fragment></LangProvider>;

  // ── 결제 결과 라우팅 ──
  if (window.location.pathname === '/payment/success') {
    return <PaymentResult user={user} type="success" />;
  }
  if (window.location.pathname === '/payment/fail') {
    return <PaymentResult user={user} type="fail" />;
  }

  // ── 고객 공유 링크 라우팅 ──
  if (window.location.pathname.startsWith('/share/')) {
    const shareId = window.location.pathname.split('/share/')[1];
    return <ShareView shareId={shareId} />;
  }

  // ── 관리자 대시보드 라우팅 ──
  if (window.location.pathname === '/admin') {
    if (!isAdmin(user)) {
      return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', flexDirection: 'column', gap: '12px',
                      color: '#52525B', fontSize: '1.1rem' }}>
          <div style={{ fontSize: '40px' }}>🚫</div>
          <p style={{ fontWeight: '700' }}>접근 권한이 없습니다.</p>
          <button onClick={() => window.location.href = '/'}
            style={{ padding: '8px 20px', borderRadius: '8px', border: 'none',
                     background: '#FF6F0F', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>
            홈으로
          </button>
        </div>
      );
    }
    return <AdminDashboard user={user} onBack={() => window.location.href = '/'} />;
  }

  const selectedItem = (categories || []).flatMap(c => c.items).find(i => i.id === selectedId);

  // 레시피 선택 (모바일이면 상세 화면으로 전환)
  const handleSelectRecipe = (id) => {
    setSelectedId(id);
    if (isMobile) setShowRecipeDetail(true);
  };

  const handleSaveRecipe = (savedItem, categoryName) => {
    const prevItem = (categories || []).flatMap(c => c.items || []).find(i => i.id === savedItem.id);   // V141 저장 전 모습
    // V150: 같은 카테고리에서 고쳐 저장하면 자리 그대로 (예전엔 고칠 때마다 카테고리 맨 아래로 내려갔다)
    const prevCat = prevItem ? (categories || []).find(c => (c.items || []).some(i => i.id === savedItem.id)) : null;
    const prevIdx = prevCat ? prevCat.items.findIndex(i => i.id === savedItem.id) : -1;
    let next = (categories || []).map(c => ({ ...c, items: [...c.items] }));
    next.forEach(c => { c.items = c.items.filter(i => i.id !== savedItem.id); });
    const targetCat = next.find(c => c.name === categoryName);
    if (targetCat) {
      // V150: 새로 만든 복사본은 원본(같은 버전 묶음) 바로 다음에 넣는다
      if (prevCat && prevCat.name === categoryName && prevIdx >= 0) targetCat.items.splice(prevIdx, 0, savedItem);
      else if (!prevItem && savedItem.copiedFrom?.id) targetCat.items.splice(insertIndexForCopy(targetCat.items, savedItem.copiedFrom.id), 0, savedItem);
      else targetCat.items.push(savedItem);
    }
    else next.push({ name: categoryName, items: [savedItem] });
    // V141: 반제품 이름을 바꾸면(불고기 → 불고기(시제품소스)) 그 반제품을 쓰는 레시피의 식재료 이름과
    //       단가표의 반제품 줄 이름도 같이 바꾼다. 예전엔 단가표에 새 줄이 하나 더 생기고, 쓰는 레시피는 옛 이름·옛 원가를 봤다
    let curPrices = prices || [];
    if (prevItem && prevItem.type === 'semi' && savedItem.type === 'semi') {
      const rn = renameSemiEverywhere(next, curPrices, { oldName: prevItem.name, newName: savedItem.name, newZh: savedItem.name_zh || '', recipeId: savedItem.id });
      if (rn.touched || rn.priceRow !== 'none') { next = rn.categories; curPrices = rn.prices; }
    }
    updateCategories(next);
    categoriesRef.current = next;

    // V115: 새로 만든 카테고리 이름도 중국어로 채운다 (목록·편집 화면은 병기)
    if (!targetCat) {
      translateMany([categoryName], 'text').then(map => {
        const zh = map.get(String(categoryName).trim());
        if (!zh) return;
        let changed = false;
        const patched = (categoriesRef.current || []).map(c => {
          if (c.name !== categoryName || c.name_zh) return c;
          changed = true;
          return { ...c, name_zh: zh };
        });
        if (changed) {
          categoriesRef.current = patched;
          updateCategories(patched);
        }
      }).catch(() => {});
    }

    // V109: 메뉴명·메모 중국어 — 저장은 이미 끝났다. 번역은 뒤에서 채운다.
    // 실패하면 한국어만 보일 뿐 아무것도 안 깨진다 (허페이 VPN 전제).
    fillRecipeZh(savedItem).then(patch => {
      if (!patch) return;
      let changed = false;
      const patched = (categoriesRef.current || []).map(c => ({
        ...c,
        items: (c.items || []).map(i => {
          if (i.id !== savedItem.id) return i;
          // 번역하는 사이에 한국어를 또 고쳤거나 사람이 중국어를 넣었다면 이 번역은 버린다
          const p = {};
          if (patch.name_zh && !i.name_zh && String(i.name || '').trim() === String(savedItem.name || '').trim()) p.name_zh = patch.name_zh;
          if (patch.note_zh && !i.note_zh && String(i.note || '').trim() === String(savedItem.note || '').trim()) p.note_zh = patch.note_zh;
          if (!Object.keys(p).length) return i;
          changed = true;
          return { ...i, ...p };
        }),
      }));
      if (changed) {
        categoriesRef.current = patched;
        updateCategories(patched);
        if (savedItem.type !== 'menu') applySemiZhToPrices(patched.flatMap(c => c.items || []));   // V122
      }
    }).catch(() => {});

    if (savedItem.type === 'semi' && prices) {
      // 반제품 원가 → 단가표 줄 (V125 소수 둘째 자리, V122 중국어). V141: 계산은 utils/semiRename.syncSemiPrice로 옮김
      const updatedPrices = syncSemiPrice(curPrices, savedItem);
      pricesRef.current = updatedPrices;
      updatePrices(updatedPrices);
    }
    setSelectedId(savedItem.id);
    if (isMobile) setShowRecipeDetail(true);
    setEditorOpen(false);
    setEditingItem(null);
    setCopyingFrom(null);
  };

  // V122: 반제품 레시피는 식재료로 쓰려고 단가표에도 한 줄로 들어간다. 레시피의 중국어 이름을 그 줄에도 채운다.
  //       (예전엔 그 줄에 중국어가 없어서 레시피 식재료 목록·발주서에 '삶은 닭'처럼 한국어만 보였다)
  const applySemiZhToPrices = (recipes) => {
    const { next, changed } = semiZhPatch(pricesRef.current || [], recipes);
    if (changed) {
      pricesRef.current = next;
      updatePrices(next);
    }
    return changed;
  };

  // V109: 기존 레시피 중국어 일괄 채우기 — 예전 레시피는 다시 저장하기 전엔 중국어가 없다.
  // 이미 값이 있는 칸은 건드리지 않는다 (사람이 고친 번역 보호).
  // V122: 레시피 식재료(반제품 이름·단가표 품목)의 중국어도 채우고, 못 채운 것은 이름을 알려준다.
  const handleFillRecipesZh = async () => {
    const all = (categoriesRef.current || []).flatMap(c => c.items || []);
    const nameOf = r => String(r?.name || '').trim();
    const noteOf = r => String(r?.note || '').trim();
    const needName = all.filter(r => nameOf(r) && !r.name_zh).map(nameOf);
    const needNote = all.filter(r => noteOf(r) && !r.note_zh).map(noteOf);
    // V111: 카테고리명도 중국어로 (예: "제품 — 소스 / 양념")
    const needCat = (categoriesRef.current || []).filter(c => nameOf(c) && !c.name_zh).map(nameOf);
    const recipeTotal = all.filter(r => (nameOf(r) && !r.name_zh) || (noteOf(r) && !r.note_zh)).length + needCat.length;

    // V122: 레시피 식재료 중 중국어가 없는 이름 (단가표 name_zh도, 같은 이름 레시피의 중국어도 없는 것)
    const missingIngs = () => missingIngredientZh(pricesRef.current || [], (categoriesRef.current || []).flatMap(c => c.items || []));
    const ingBefore = missingIngs();
    const total = recipeTotal + ingBefore.length;
    if (total === 0) return { filled: 0, total: 0, failed: [] };

    let filled = 0;
    if (recipeTotal > 0) filled += await fillRecipeTexts(all, needName, needNote, needCat, nameOf, noteOf);

    // 반제품 레시피 이름 → 단가표 줄
    applySemiZhToPrices((categoriesRef.current || []).flatMap(c => c.items || []));

    // 단가표에 있는 식재료 → 품목 번역 (빈 중국어 칸만 채운다)
    const left = missingIngs();
    const rows = left.map(n => findPriceByName(pricesRef.current || [], n)).filter(Boolean);
    if (rows.length) {
      try {
        const { next: translated } = await fillPriceZh(rows);
        const { next: merged, changed } = mergePriceZh(pricesRef.current || [], translated, PRICE_ZH_FIELDS.map(([, zf]) => zf));
        if (changed) {
          pricesRef.current = merged;
          updatePrices(merged);
        }
      } catch (e) { /* 아래 못 채운 목록에 남는다 */ }
    }
    const ingAfter = missingIngs();
    filled += ingBefore.length - ingAfter.length;

    // 못 채운 것 — 화면에 이름을 보여줘서 원인을 알 수 있게
    const failed = listMissingRecipeZh(categoriesRef.current || []);
    for (const n of ingAfter) failed.push({ name: n, what: 'ingredient' });
    if (failed.length) console.info('[중국어 일괄] 못 채운 것:', failed);
    return { filled, total, failed };
  };

  // V109~: 레시피 이름·메모·카테고리 번역 (V122: 일괄 채우기에서 떼어 냄)
  const fillRecipeTexts = async (all, needName, needNote, needCat, nameOf, noteOf) => {
    const [nameMap, noteMap, catMap] = await Promise.all([
      needName.length ? translateMany(needName, 'menu') : Promise.resolve(new Map()),
      needNote.length ? translateMany(needNote, 'recipe') : Promise.resolve(new Map()),
      needCat.length ? translateMany(needCat, 'text') : Promise.resolve(new Map()),
    ]);

    let filled = 0;
    const patched = (categoriesRef.current || []).map(c => ({
      ...c,
      ...((nameOf(c) && !c.name_zh && catMap.get(nameOf(c))) ? (filled++, { name_zh: catMap.get(nameOf(c)) }) : {}),
      items: (c.items || []).map(i => {
        const p = {};
        if (nameOf(i) && !i.name_zh && nameMap.get(nameOf(i))) p.name_zh = nameMap.get(nameOf(i));
        if (noteOf(i) && !i.note_zh && noteMap.get(noteOf(i))) p.note_zh = noteMap.get(noteOf(i));
        if (!Object.keys(p).length) return i;
        filled++;
        return { ...i, ...p };
      }),
    }));
    if (filled > 0) {
      categoriesRef.current = patched;
      await updateCategories(patched);
    }
    return filled;
  };
  const allRecipes = (categories || []).flatMap(c => c.items || []);

  // V141: 이미 끊긴 반제품 연결 다시 잇기 — 레시피 화면에서 '이 반제품으로 연결'을 누르면
  //       옛 이름(불고기)을 쓰는 모든 레시피를 고른 반제품(불고기(시제품소스))으로 바꾸고, 단가표의 옛 줄은 정리한다
  const handleRelinkSemi = (oldName, targetId) => {
    const cats = categoriesRef.current || categories || [];
    const target = cats.flatMap(c => c.items || []).find(r => r.id === targetId && r.type === 'semi');
    if (!target) return 0;
    const rn = renameSemiEverywhere(cats, pricesRef.current || prices || [], { oldName, newName: target.name, newZh: target.name_zh || '' });
    if (!rn.touched) return 0;
    categoriesRef.current = rn.categories;
    updateCategories(rn.categories);
    const ps = syncSemiPrice(rn.prices, target);   // 그 반제품의 지금 원가로
    pricesRef.current = ps;
    updatePrices(ps);
    return rn.touched;
  };

  const handleDeleteRecipe = (id) => {
    const next = (categories || [])
      .map(c => ({ ...c, items: c.items.filter(i => i.id !== id) }))
      .filter(c => c.items.length > 0);
    updateCategories(next);
    setSelectedId(null);
    setShowRecipeDetail(false);
    setEditorOpen(false);
    setEditingItem(null);
    setCopyingFrom(null);
  };

  const handleEditClick = () => {
    setCopyingFrom(null);
    setEditingItem(selectedItem);
    setEditorOpen(true);
  };

  // V153: 반제품 이름이 식재료와 겹친 것 고치기 — 반제품 이름을 바꾸고, 덮인 식재료 줄은 되돌리고, 고른 레시피만 새 반제품으로
  const handleFixSemiClash = (opts) => {
    const res = fixSemiClash(categoriesRef.current || categories || [], pricesRef.current || prices || [], opts);
    if (!res) return false;
    categoriesRef.current = res.categories;
    updateCategories(res.categories);
    pricesRef.current = res.prices;
    updatePrices(res.prices);
    setSelectedId(res.recipe.id);
    // 새 이름의 중국어 (뒤에서) — 반제품이라 단가표 줄에도
    fillRecipeZh(res.recipe).then(patch => {
      if (!patch || !patch.name_zh) return;
      let changed = false;
      const patched = (categoriesRef.current || []).map(c => ({
        ...c,
        items: (c.items || []).map(i => {
          if (i.id !== res.recipe.id || i.name_zh || String(i.name || '').trim() !== res.recipe.name) return i;
          changed = true;
          return { ...i, name_zh: patch.name_zh };
        }),
      }));
      if (changed) {
        categoriesRef.current = patched;
        updateCategories(patched);
        applySemiZhToPrices(patched.flatMap(c => c.items || []));
      }
    }).catch(() => {});
    return true;
  };
  // V153: 그 이름의 가장 최근 발주 (덮인 식재료 단가를 다시 넣을 때 참고)
  const lastOrderOf = (name) => {
    const list = [...(orderHistory || [])].sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
    for (const e of list) {
      const it = (e.items || []).find(x => sameIngredientName(x && x.name, name));
      if (it) return { date: e.date, price: it.price, unit: it.unit, spec: it.spec };
    }
    return null;
  };

  // V150: 지금 보는 레시피를 복사해서 새 버전으로 — 편집 창이 'ver N' 이름으로 채워져 열린다 (저장해야 생김)
  const handleCopyClick = () => {
    if (!selectedItem) return;
    setEditingItem(null);
    setCopyingFrom(selectedItem);
    setEditorOpen(true);
  };

  const handleAddToPurchaseOrder = (newQty) => {
    // V142: 발주 수량·필요량은 단가표 '줄' 키로 (같은 이름이 발주처별로 여러 줄이면 원가 계산과 같은 첫 줄에)
    const rows = Object.entries(newQty).map(([name, qty]) => {
      const row = findOrderRow(prices || [], name);
      return { key: row ? orderKeyOf(row) : name, row, qty };
    });
    setOrderNeed(prev => {
      const next = { ...prev };
      rows.forEach(({ key, qty }) => { next[key] = qty; });
      return next;
    });
    // V135: 중국 매장은 발주 수량 = 규격 묶음 수 (감자 10근 묶음, 필요 23근 → 3)
    const cn = storeCountry === 'CN';
    setOrderQty(prev => {
      const next = { ...prev };
      rows.forEach(({ key, row, qty }) => {
        next[key] = cn ? needToOrderQty(qty, orderPackInfo(row, true)) : Math.ceil(qty);
      });
      return next;
    });
    setCurrentView('purchase');
  };

  // V58: orderEmoji 제거 (Lucide 아이콘으로 통합)
  const projectsTab = effectiveMode === 'interior'
    ? [{ id: 'projects', label: '프로젝트' }]
    : [];

  // ── 글로벌 재고 알림 배지 (food 모드만) ──
  const inventoryAlertCount = (() => {
    if (effectiveMode !== 'food') return 0;
    if (!prices || prices.length === 0) return 0;
    const orderUsage = calcAvgUsage(orderHistory || [], 3);
    const recipeUsage = calcRecipeBasedUsage(categories || [], prices || [], payrollSettings?.expectedMonthlyServings);
    const merged = mergeUsage(orderUsage, recipeUsage);
    return countNeedOrder(prices, inventory, orderHistory, merged);
  })();

  // V50/V58: 탭별 권한 카테고리 + Lucide 아이콘
  const ALL_TABS = [
    { id: 'home',     Icon: Home,         label: '홈',          cat: 'operations'  },
    { id: 'recipes',  Icon: BookOpen,     label: terms.recipe,  cat: 'recipes'     },
    // V149 오늘의 작업 — 레시피 보기 권한이 있으면 같이 보인다 (식음료 업종만)
    ...(effectiveMode === 'food' ? [{ id: 'work', Icon: ListChecks, label: '오늘의 작업', cat: 'recipes' }] : []),
    { id: 'orders',   Icon: Users,        label: terms.orderTab, cat: 'transactions' },
    { id: 'purchase', Icon: FileText,     label: '발주서',      cat: 'orderHistory' },
    { id: 'receiving', Icon: PackageCheck, label: '입고',       cat: 'orderHistory' },   // V158
    { id: 'prices',   Icon: Coins,        label: '단가표',      cat: 'priceList'   },
    { id: 'profit',   Icon: BarChart3,    label: '운영',        cat: 'operations'  },
    { id: 'purchaseLog', Icon: Package,   label: '식재료',      cat: 'purchaseLog' },
    { id: 'groupBuying', Icon: ShoppingCart, label: '공동구매', cat: null },
    { id: 'inventory',Icon: Boxes,        label: '재고관리', badge: inventoryAlertCount, cat: 'inventory' },
    { id: 'payroll',  Icon: Wallet,       label: '인건비',      cat: 'payroll'     },
    { id: 'tax',      Icon: Receipt,      label: '세무회계',    cat: 'tax'         },
    ...projectsTab.map(t => ({ ...t, Icon: FolderKanban, cat: 'operations' })),
    { id: 'manual',   Icon: Printer,      label: '매장 안내서', cat: 'priceList'   },
    { id: 'settings', Icon: Settings,     label: '설정',        cat: null },
  ];
  const TABS = isManagerMode
    ? ALL_TABS.filter(t => {
        if (t.id === 'groupBuying') return false;  // 공동구매는 owner 결제 기반이라 매니저 모드 숨김
        if (!t.cat) return true;                    // home/settings 등 항상 표시 (cat=null)
        return hasPermission('read', t.cat);
      })
    : ALL_TABS;

  // 모바일 콘텐츠 높이 (상단 헤더 54px + 하단 탭 56px)
  const contentH = isMobile ? 'calc(100vh - 54px - 56px)' : 'calc(100vh - 54px)';

  // ── V102: 무료 체험(3일) 종료 → 앱 전체 잠금, 이용권 페이지만 노출 ──
  // 매니저 모드는 매장 owner 권한으로 접근하므로 잠그지 않음.
  if (!planLoading && trialExpired && !isManagerMode) {
    return (
      <div style={{ minHeight: '100vh', background: '#f0f2f5' }}>
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '14px 20px',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, color: '#18181B' }}>
              <img src="/semfit_symbol_t.png" alt="" style={{ width: 26, height: 26, objectFit: 'contain' }} />
              {L0('셈핏', 'SEMFIT')}
            </div>
            <button onClick={() => { clearKeyCache(); signOut(auth); }}
              style={{
                padding: '7px 14px', borderRadius: '10px', border: '1px solid #e5e7eb',
                background: '#fff', color: '#52525B', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              }}>
              {L0('로그아웃', '退出登录')}
            </button>
          </div>
          {/* V147: 매니저로 들어간 매장이 있으면 그 매장으로 바로 (직원 계정이 잠금 화면에 갇히지 않게) */}
          {managedStores && managedStores.length > 0 && (
            <div data-locked-stores style={{ margin: '4px 20px 12px', background: '#fff', border: '1.5px solid #FF6F0F',
                          borderRadius: 12, padding: '12px 16px', fontSize: 14, color: '#18181B' }}>
              <div style={{ fontWeight: 800, marginBottom: 8 }}>{L0('👥 일하는 매장으로 가기', '👥 进入我工作的门店')}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {managedStores.map(st => (
                  <button key={st.ownerUid} onClick={() => switchStore(st.ownerUid)}
                    style={{ padding: '9px 16px', borderRadius: 10, border: 'none', background: '#FF6F0F', color: '#fff',
                             fontSize: 14, fontWeight: 800, cursor: 'pointer' }}>
                    {st.ownerStoreName || st.ownerEmail || st.ownerUid.slice(0, 8)}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{
            margin: '4px 20px 0', background: '#FFF7ED', border: '1px solid #FDBA74',
            borderRadius: '12px', padding: '12px 16px', fontSize: '13px', color: '#9A3412',
            fontWeight: 600, textAlign: 'center', lineHeight: 1.6,
          }}>
            {zh0 ? <>⏰ 3天免费试用已结束。您输入的数据都安全保存着 ——<br/>登记使用码后就能继续使用。</>
                 : <>⏰ 3일 무료 체험이 끝났어요. 입력하신 데이터는 안전하게 보관 중이에요 —<br/>
            이용권 코드를 등록하면 바로 이어서 쓸 수 있어요.</>}
          </div>
          <SubscriptionPage user={user} currentPlan={plan} isLifetime={isLifetime} onClose={() => {}} />
        </div>
      </div>
    );
  }

  return (
    <LangProvider user={user} ownerUid={effectiveOwnerUid}>
    <div key={uiLang} style={{ minHeight: '100vh', background: '#FAFAFA' }}>

      {/* ── 헤더 (V58 풀 리디자인) ── */}
      <header style={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        color: '#18181B', padding: '0 20px', height: '56px',
        display: 'flex', alignItems: 'center', gap: '12px',
        position: 'sticky', top: 0, zIndex: 200,
        borderBottom: '1px solid #F4F4F5',
      }}>
        <h1
          onClick={() => {
            const next = logoClickCount + 1;
            setLogoClickCount(next);
            if (next >= 1) { setShowEasterEgg(true); setLogoClickCount(0); }
          }}
          title="🤫"
          style={{
            fontSize: '18px', fontWeight: '800', flexShrink: 0,
            cursor: 'pointer', userSelect: 'none',
            transition: 'transform 0.15s',
            display: 'flex', alignItems: 'center', gap: 8,
            color: '#18181B', letterSpacing: '-0.02em',
          }}
          onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
          onMouseLeave={e => e.currentTarget.style.transform = ''}
        >
          {/* V58: 새 오렌지 로고 (원래 색 그대로, 화이트 헤더에 맞게) */}
          <img src="/semfit_symbol_t.png" alt=""
               style={{ width: 28, height: 28, objectFit: 'contain' }} />
          <span><LangText ko="셈핏" zh="SEMFIT" /></span>
        </h1>

        {/* ── 모드 선택 드롭다운 (V30: MODES 2개 이상일 때만 노출) ── */}
        {MODES.length > 1 && (
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setModeDropdownOpen(o => !o)}
            title="업종 모드 변경"
            style={{
              display: 'flex', alignItems: 'center', gap: '5px',
              padding: '5px 10px', borderRadius: '8px',
              border: '1.5px solid rgba(255,255,255,0.45)',
              background: modeDropdownOpen ? 'rgba(255,255,255,0.2)' : 'transparent',
              color: '#fff', fontSize: '13px', fontWeight: '700',
              cursor: 'pointer', whiteSpace: 'nowrap',
              transition: 'background 0.15s',
            }}>
            <span>{currentModeInfo.icon}</span>
            {!isMobile && <span>{currentModeInfo.label}</span>}
            <span style={{ fontSize: '10px', opacity: 0.75 }}>▾</span>
          </button>
          {modeDropdownOpen && (
            <>
              {/* 바깥 클릭 닫기 오버레이 */}
              <div
                style={{ position: 'fixed', inset: 0, zIndex: 299 }}
                onClick={() => setModeDropdownOpen(false)}
              />
              <div style={{
                position: 'absolute', top: '100%', left: 0, marginTop: '6px',
                background: '#fff', borderRadius: '12px',
                boxShadow: '0 8px 30px rgba(0,0,0,0.18)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden', zIndex: 300, minWidth: '150px',
              }}>
                <div style={{ padding: '6px 0' }}>
                  {MODES.map(mode => (
                    <button
                      key={mode.id}
                      onClick={() => handleModeChange(mode.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '10px',
                        width: '100%', padding: '10px 16px',
                        border: 'none', background: effectiveMode === mode.id ? '#FAFAFA' : 'transparent',
                        color: effectiveMode === mode.id ? '#FF6F0F' : '#374151',
                        fontSize: '13px', fontWeight: effectiveMode === mode.id ? '700' : '500',
                        cursor: 'pointer', textAlign: 'left',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={e => { if (effectiveMode !== mode.id) e.currentTarget.style.background = '#f9fafb'; }}
                      onMouseLeave={e => { if (effectiveMode !== mode.id) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <span style={{ fontSize: '18px', width: '24px', textAlign: 'center' }}>{mode.icon}</span>
                      <span style={{ flex: 1 }}>{mode.label}</span>
                      {effectiveMode === mode.id && (
                        <span style={{ color: '#18181B', fontSize: '14px' }}>✓</span>
                      )}
                    </button>
                  ))}
                </div>
                <div style={{
                  borderTop: '1px solid #f3f4f6', padding: '8px 12px',
                  fontSize: '11px', color: '#9ca3af', textAlign: 'center',
                }}>
                  모드 전환은 모든 플랜 무료
                </div>
              </div>
            </>
          )}
        </div>
        )}

        {/* PC 탭 (V58: 라이트 톤, 활성 시 오렌지 강조) */}
        {!isMobile && (
          <div style={{ display: 'flex', gap: '2px', flex: 1, marginLeft: '16px',
                        overflowX: 'auto', overflowY: 'hidden', minWidth: 0 }}>
            {TABS.map(tab => {
              const active = currentView === tab.id;
              return (
              <button key={tab.id} onClick={() => setCurrentView(tab.id)}
                data-tour={`tab-${tab.id}`}
                style={{
                  padding: '8px 12px', borderRadius: '10px', fontSize: '14px',
                  fontWeight: active ? '700' : '500', cursor: 'pointer', border: 'none',
                  background: active ? '#FAFAFA' : 'transparent',
                  color: active ? '#FF6F0F' : '#52525B',
                  transition: 'all 0.15s', position: 'relative',
                  whiteSpace: 'nowrap', flexShrink: 0,
                  letterSpacing: '-0.01em',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
                onMouseEnter={e => { if (!active) { e.currentTarget.style.background = '#F4F4F5'; e.currentTarget.style.color = '#18181B'; } }}
                onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#52525B'; } }}>
                {tab.Icon && <tab.Icon size={16} strokeWidth={active ? 2.5 : 2} />}
                <T k={tab.label} />
                {tab.badge > 0 && (
                  <span style={{
                    position: 'absolute', top: 2, right: 2,
                    minWidth: 16, height: 16, padding: '0 4px',
                    borderRadius: 8, background: '#71717A', color: '#fff',
                    fontSize: 10, fontWeight: 800, lineHeight: '16px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                  }}>{tab.badge > 9 ? '9+' : tab.badge}</span>
                )}
              </button>
              );
            })}
          </div>
        )}

        <div style={{ flex: isMobile ? 1 : 0 }} />

        {/* 도움말 메뉴 (V58: 라이트 outline) */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setHelpMenuOpen(o => !o)}
            title={L0('튜토리얼 다시 보기', '再看一遍使用说明')}
            style={{
              padding: '7px 12px', borderRadius: '10px',
              border: '1px solid #F4F4F5',
              background: helpMenuOpen ? '#F4F4F5' : '#fff',
              color: '#52525B',
              fontSize: '13px', fontWeight: 600, cursor: 'pointer',
              transition: 'all 0.15s',
            }}>
            <T k="도움말" />
          </button>
          {helpMenuOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 299 }}
                onClick={() => setHelpMenuOpen(false)} />
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 6,
                background: '#fff', borderRadius: 12, minWidth: 220,
                boxShadow: '0 8px 30px rgba(0,0,0,0.2)',
                border: '1px solid #e5e7eb', overflow: 'hidden', zIndex: 300,
              }}>
                {!isMobile && (
                <button
                  onClick={() => {
                    setHelpMenuOpen(false);
                    setShowGuidedTour(true);
                  }}
                  style={{
                    width: '100%', padding: '12px 16px', textAlign: 'left',
                    border: 'none', background: 'transparent', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, color: '#7C3AED',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  <LangText ko="🚀 인터랙티브 투어 (실제 화면에서 따라하기)" zh="🚀 互动导览（在真实页面上跟着点）" />
                </button>
                )}
                <button
                  onClick={() => {
                    setHelpMenuOpen(false);
                    resetOnboarding();
                    setShowOnboarding(true);
                  }}
                  style={{
                    width: '100%', padding: '12px 16px', textAlign: 'left',
                    border: 'none', borderTop: '1px solid #f3f4f6',
                    background: 'transparent', cursor: 'pointer',
                    fontSize: 13, fontWeight: 700, color: '#18181B',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  <LangText ko="🎓 신규 사용자 환영 가이드" zh="🎓 使用引导" />
                </button>
                {hasTutorial(currentView) && (
                  <button
                    onClick={() => {
                      setHelpMenuOpen(false);
                      setTabTutorial(currentView);
                    }}
                    style={{
                      width: '100%', padding: '12px 16px', textAlign: 'left',
                      border: 'none', borderTop: '1px solid #f3f4f6',
                      background: 'transparent', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, color: '#374151',
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                    <LangText ko="📖 현재 화면 도움말" zh="📖 本页面说明" />
                  </button>
                )}
                <button
                  onClick={() => {
                    setHelpMenuOpen(false);
                    resetAllHints();
                    resetAllTutorials();
                    window.location.reload();
                  }}
                  style={{
                    width: '100%', padding: '12px 16px', textAlign: 'left',
                    border: 'none', borderTop: '1px solid #f3f4f6',
                    background: 'transparent', cursor: 'pointer',
                    fontSize: 12, color: '#6b7280',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                  <LangText ko="🔄 모든 힌트·튜토리얼 다시 보기" zh="🔄 重新显示所有提示和说明" />
                </button>
              </div>
            </>
          )}
        </div>

        {/* V50: 매장 전환 셀렉터 — 매니저로 들어간 매장 있을 때만 표시 */}
        {managedStores && managedStores.length > 0 && (
          <StoreSwitcher
            user={user}
            effectiveOwnerUid={effectiveOwnerUid}
            isManagerMode={isManagerMode}
            currentStore={currentStore}
            managedStores={managedStores}
            switchStore={switchStore}
          />
        )}

        {/* 플랜 뱃지 — 본인 모드에서만 (V58: 톤 정리) */}
        {!isManagerMode && (
        <button onClick={() => setShowSubscription(true)}
          style={{
            padding: '5px 12px', borderRadius: '999px', border: 'none', cursor: 'pointer',
            background: PLANS[plan]?.color || '#71717A',
            color: '#fff', fontSize: '11px', fontWeight: '800', flexShrink: 0,
            letterSpacing: '0.02em',
          }}>
          {trialActive ? <LangText ko={`체험 D-${trialDaysLeft}`} zh={`试用 剩${trialDaysLeft}天`} /> : isApproved ? <LangText ko="Pro · 승인" zh="Pro · 已批准" /> : (PLANS[plan]?.label || <LangText ko="무료" zh="免费" />)}
        </button>
        )}

        {/* V105: 한/中 언어 토글 — 설정 탭이 가로 스크롤로 가려져도 항상 보이는 자리 */}
        {/* V121: 저장 상태 — 저장 중 / 저장됨 / 저장 안 됨(누르면 다시 시도) */}
        <SaveIndicator state={saveState} onRetry={retrySaves} compact={isMobile} />

        <LangToggle compact={isMobile} />

        {/* 이메일 (PC만, V58: 라이트 톤) */}
        {!isMobile && (
          <span style={{ fontSize: '12px', color: '#71717A', marginRight: '4px', fontWeight: 500 }}>
            {user.email}
          </span>
        )}

        <button onClick={() => { clearKeyCache(); signOut(auth); }}
          style={{
            padding: isMobile ? '7px 12px' : '7px 14px',
            borderRadius: '10px',
            border: '1px solid #F4F4F5',
            background: '#fff', color: '#52525B',
            fontSize: '13px', fontWeight: 600, cursor: 'pointer', flexShrink: 0,
            transition: 'all 0.15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = '#F4F4F5'; e.currentTarget.style.color = '#18181B'; }}
          onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#52525B'; }}>
          <T k="로그아웃" />
        </button>
      </header>

      {/* ── V121: 지난번 서버에 못 보낸 내용이 이 기기에 남아 있으면 복원할지 묻는다 ── */}
      <UnsavedBanner backup={unsavedBackup} onRestore={restoreUnsaved} onDiscard={discardUnsaved} />

      {/* ── V102: 무료 체험 카운트다운 배너 — 체험 중인 사용자에게만 ── */}
      {trialActive && !isManagerMode && (
        <div
          onClick={() => setShowSubscription(true)}
          style={{
            background: '#FFF7ED', borderBottom: '1px solid #FDBA74',
            padding: '8px 16px', fontSize: '12.5px', color: '#9A3412',
            textAlign: 'center', cursor: 'pointer', fontWeight: 600,
          }}>
          <LangText
            ko={<>⏰ 무료 체험이 <strong>{trialDaysLeft}일</strong> 남았어요 — <span style={{ textDecoration: 'underline' }}>평생 이용권 런칭 특가 39,000원 (30명 한정)</span>으로 계속 이용하기</>}
            zh={<>⏰ 免费试用还剩 <strong>{trialDaysLeft}天</strong> —— <span style={{ textDecoration: 'underline' }}>点这里查看使用券</span></>} />
        </div>
      )}

      {/* ── 콘텐츠 (V41: display:flex 제거 — 자식이 폭 100% 차지) ── */}
      <div style={{ height: contentH, overflowY: 'auto', overflowX: 'hidden', WebkitOverflowScrolling: 'touch' }}>

        {/* 홈 / 대시보드 */}
        {currentView === 'home' && (
          <HomeView
            tabs={TABS.map(t => t.id)}
            storeName={isManagerMode ? (currentStore?.ownerStoreName || '') : ''}
            onAddToPurchaseOrder={handleAddToPurchaseOrder}
            workToday={workToday}
            workChecks={workChecks || {}}
            user={user}
            profitLoss={profitLoss || {}}
            orderHistory={orderHistory || []}
            prices={prices || []}
            inventory={inventory || {}}
            categories={categories || []}
            employees={employees || []}
            schedules={schedules || {}}
            payrollSettings={payrollSettings || {}}
            onNavigate={(tabId) => setCurrentView(tabId)}
          />
        )}

        {/* 레시피 탭 */}
        {currentView === 'recipes' && (
          <>
            {/* 모바일: 사이드바 OR 상세 */}
            {isMobile ? (
              <>
                {!showRecipeDetail ? (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <Sidebar
                      categories={categories || []}
                      selectedId={selectedId}
                      onSelect={handleSelectRecipe}
                      onAddRecipe={() => { setEditingItem(null); setCopyingFrom(null); setEditorOpen(true); }}
                      onReorder={updateCategories}
                      isMobile={true}
                      ownerUid={effectiveOwnerUid}
                      onFillAllZh={handleFillRecipesZh}
                      terms={terms}
                    />
                  </div>
                ) : (
                  <RecipeView
                    item={selectedItem}
                    prices={prices || []}
                    onEdit={handleEditClick}
                    isMobile={true}
                    onBack={() => { setShowRecipeDetail(false); setSelectedId(null); }}
                    terms={terms}
                    user={user}
                    profitLoss={profitLoss || {}}
                    payrollSettings={payrollSettings || {}}
                    onSettingsChange={updatePayrollSettings}
                    allRecipes={allRecipes}
                    onFillAllZh={handleFillRecipesZh}
                    onRelinkSemi={handleRelinkSemi}
                    onCopy={handleCopyClick}
                    onOpenRecipe={handleSelectRecipe}
                    onFixSemiClash={handleFixSemiClash}
                    lastOrderOf={lastOrderOf}
                    categories={categories || []}
                  />
                )}
              </>
            ) : (
              /* PC: 사이드바 + 상세 나란히 — V54: flex 래퍼 추가 (V41에서 누락된 버그 수정) */
              <div style={{ display: 'flex', height: '100%' }}>
                <Sidebar
                  categories={categories || []}
                  selectedId={selectedId}
                  onSelect={handleSelectRecipe}
                  onAddRecipe={() => { setEditingItem(null); setCopyingFrom(null); setEditorOpen(true); }}
                  onReorder={updateCategories}
                  isMobile={false}
                  ownerUid={effectiveOwnerUid}
                  onFillAllZh={handleFillRecipesZh}
                  terms={terms}
                />
                <RecipeView
                  item={selectedItem}
                  prices={prices || []}
                  onEdit={handleEditClick}
                  isMobile={false}
                  terms={terms}
                  user={user}
                  profitLoss={profitLoss || {}}
                  payrollSettings={payrollSettings || {}}
                  onSettingsChange={updatePayrollSettings}
                  allRecipes={allRecipes}
                  onFillAllZh={handleFillRecipesZh}
                  onRelinkSemi={handleRelinkSemi}
                  onCopy={handleCopyClick}
                  onOpenRecipe={handleSelectRecipe}
                  onFixSemiClash={handleFixSemiClash}
                  lastOrderOf={lastOrderOf}
                  categories={categories || []}
                />
              </div>
            )}
          </>
        )}

        {currentView === 'orders' && (
          <OrderSheet
            categories={categories || []}
            prices={prices || []}
            selectedServings={selectedServings}
            onSelectedServingsChange={setSelectedServings}
            onAddToPurchaseOrder={handleAddToPurchaseOrder}
            terms={terms}
          />
        )}

        {currentView === 'purchase' && (
          <PurchaseOrder
            prices={prices || []}
            onPricesChange={updatePrices}
            categories={categories || []}
            orderQty={orderQty}
            onOrderQtyChange={setOrderQty}
            orderNeed={orderNeed}
            onOrderNeedChange={setOrderNeed}
            orderHistory={orderHistory || []}
            onSaveOrderHistory={saveOrderHistory}
            onDeleteOrderHistory={deleteOrderHistory}
            onMergeOrderHistory={mergeOrderHistory}
            onUpdateOrderHistory={updateOrderHistory}
            ownerUid={effectiveOwnerUid}
            canChangeCountry={!isManagerMode}
          />
        )}

        {currentView === 'receiving' && (
          <ReceivingPage
            prices={prices || []}
            orderHistory={orderHistory || []}
            receivings={receivings}
            loading={receivingsLoading || dataLoading}
            user={user}
            isMobile={isMobile}
            canDelete={!isManagerMode}
            onSaveReceiving={saveReceiving}
            onPatchReceiving={patchReceiving}
            onDeleteReceiving={removeReceiving}
            onSavePhoto={(id, photo) => saveReceivingPhoto(id, photo, String(user?.displayName || String(user?.email || '').split('@')[0] || ''))}
            onLoadPhoto={loadReceivingPhoto}
            onRemovePhoto={removeReceivingPhoto}
            onPricesChange={updatePrices}
            onUpdateOrders={updateOrderHistoryMany}
          />
        )}

        {currentView === 'prices' && (
          <PriceManager
            prices={prices || []}
            onPricesChange={updatePrices}
            categories={categories || []}
            onCategoriesChange={updateCategories}
            user={user}
          />
        )}

        {/* V149 오늘의 작업 */}
        {currentView === 'work' && (
          <WorkToday
            categories={categories || []}
            prices={prices || []}
            user={user}
            isMobile={isMobile}
            storeName={isManagerMode ? (currentStore?.ownerStoreName || '') : ''}
            workToday={workToday}
            workChecks={workChecks || {}}
            workHistory={workHistory || []}
            onWorkTodayChange={updateWorkToday}
            onCheck={setWorkCheck}
            onSaveHistory={saveWorkHistory}
            onDeleteHistory={deleteWorkHistory}
            employees={employees || []}
            meetings={meetings || []}
            onCommitWork={commitWork}
            autoRollover={false}   // V156 날 넘기기는 App이 한다 (어느 화면에 있든)
          />
        )}

        {currentView === 'manual' && (
          <ManualPage
            categories={categories || []}
            prices={prices || []}
            user={user}
            ownerUid={effectiveOwnerUid}
            onCategoriesChange={updateCategories}
          />
        )}

        {currentView === 'inventory' && (
          <InventoryManager
            prices={prices || []}
            orderHistory={orderHistory || []}
            inventory={inventory || {}}
            onInventoryChange={updateInventory}
            canAccess={canAccess}
            user={user}
            categories={categories || []}
            payrollSettings={payrollSettings || {}}
            onAddToPurchaseOrder={handleAddToPurchaseOrder}
          />
        )}

        {currentView === 'payroll' && (
          <PayrollManager
            employees={employees || []}
            schedules={schedules || {}}
            payrollSettings={payrollSettings || {}}
            profitLoss={profitLoss || {}}
            onEmployeesChange={updateEmployees}
            onSchedulesChange={updateSchedules}
            onSettingsChange={updatePayrollSettings}
            onProfitLossChange={updateProfitLoss}
            canAccess={canAccess}
            user={user}
          />
        )}

        {currentView === 'tax' && (
          <TaxCenter
            profitLoss={profitLoss || {}}
            taxData={taxData || {}}
            onTaxDataChange={updateTaxData}
            canAccess={canAccess}
            user={user}
            prices={prices || []}
            employees={employees || []}
            schedules={schedules || {}}
            payrollSettings={payrollSettings || {}}
          />
        )}

        {currentView === 'profit' && (
          <OperationsTab
            user={user}
            profitLoss={profitLoss || {}}
            onProfitLossChange={updateProfitLoss}
            orderHistory={orderHistory || []}
            receivings={receivings}
            categories={categories || []}
            prices={prices || []}
            inventory={inventory || {}}
            menuRecipeMap={menuRecipeMap || {}}
            onMenuRecipeMapChange={updateMenuRecipeMap}
            canAccess={canAccess}
          />
        )}

        {currentView === 'purchaseLog' && (
          <PurchaseLogPage user={user} prices={prices || []} />
        )}

        {currentView === 'groupBuying' && (
          <GroupBuyingPage user={user} isAdminUser={isAdmin(user)} />
        )}

        {currentView === 'settings' && (
          <SettingsPage
            user={user}
            canAccess={canAccess}
            isManagerMode={isManagerMode}
            currentStore={currentStore}
            onSignOut={() => setCurrentView('home')}
            backupData={{
              categories, prices, profitLoss, employees, schedules,
              inventory, orderHistory, payrollSettings, taxData, industry,
            }}
            restoreWriters={{
              updateCategories, updatePrices, updateProfitLoss,
              updateEmployees, updateSchedules, updateInventory,
              updatePayrollSettings,
            }}
          />
        )}
      {currentView === 'projects' && (
        <ProjectManager
          user={user}
          categories={categories || []}
          prices={prices || []}
          effectiveMode={effectiveMode}
          terms={terms}
          projects={projects || []}
          saveProject={saveProject}
          deleteProject={deleteProject}
        />
      )}
      </div>

      {/* ── 모바일 하단 탭 네비게이션 (V43: 내부 div로 스크롤 분리) ── */}
      {isMobile && (
        <nav style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, height: '56px',
          background: '#fff', borderTop: '1px solid #e5e7eb',
          zIndex: 200,
          boxShadow: '0 -2px 10px rgba(0,0,0,0.08)',
        }}>
        <div style={{
          display: 'flex', height: '100%',
          overflowX: 'auto', overflowY: 'hidden', WebkitOverflowScrolling: 'touch',
        }}>
          {TABS.map(tab => {
            const active = currentView === tab.id;
            return (
              <button key={tab.id}
                onClick={() => {
                  setCurrentView(tab.id);
                  if (tab.id === 'recipes') {
                    setShowRecipeDetail(false);
                    setSelectedId(null);
                  }
                }}
                data-tour={`tab-${tab.id}`}
                style={{
                  flex: '0 0 auto', minWidth: '68px',
                  display: 'flex', flexDirection: 'column',
                  alignItems: 'center', justifyContent: 'center',
                  border: 'none', background: 'none',
                  color: active ? '#FF6F0F' : '#A1A1AA',
                  fontSize: '12px', fontWeight: active ? '800' : '500',
                  gap: '2px', cursor: 'pointer', padding: '14px 4px',
                  borderTop: active ? '2px solid #FF6F0F' : '2px solid transparent',
                  transition: 'all 0.15s', position: 'relative',
                  whiteSpace: 'nowrap',
                  letterSpacing: '-0.01em',
                }}>
                {/* V58: Lucide 아이콘 + 배지 */}
                <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', height: 22 }}>
                  {tab.Icon && <tab.Icon size={20} strokeWidth={active ? 2.5 : 2} />}
                  {tab.badge > 0 && (
                    <span style={{
                      position: 'absolute', top: -4, right: -8,
                      minWidth: 14, height: 14, padding: '0 3px',
                      borderRadius: 7, background: '#71717A', color: '#fff',
                      fontSize: 9, fontWeight: 800, lineHeight: '14px',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    }}>{tab.badge > 9 ? '9+' : tab.badge}</span>
                  )}
                </span>
                <span style={{ whiteSpace: 'nowrap', wordBreak: 'keep-all', fontSize: 10 }}><T k={tab.label} /></span>
              </button>
            );
          })}
        </div>
        </nav>
      )}

      {/* 구독 페이지 모달 */}
      {showSubscription && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
          zIndex: 300, overflowY: 'auto',
        }} onClick={() => setShowSubscription(false)}>
          <div style={{
            background: '#f0f2f5', minHeight: '100vh', maxWidth: '960px',
            margin: '0 auto', position: 'relative',
          }} onClick={e => e.stopPropagation()}>
            <button onClick={() => setShowSubscription(false)}
              style={{
                position: 'sticky', top: '12px', left: '12px', zIndex: 10,
                margin: '12px', padding: '8px 16px', borderRadius: '8px',
                border: 'none', background: '#FF6F0F', color: '#fff',
                cursor: 'pointer', fontWeight: '700', fontSize: '13px',
              }}>
              <LangText ko="← 돌아가기" zh="← 返回" />
            </button>
            <SubscriptionPage user={user} currentPlan={plan} isLifetime={isLifetime} onClose={() => setShowSubscription(false)} />
          </div>
        </div>
      )}

      {/* 이스터에그 게임 */}
      {showEasterEgg && (
        <EasterEggGame mode={effectiveMode} onClose={() => setShowEasterEgg(false)} />
      )}

      {/* PWA 홈 화면 추가 안내 (모바일·미설치 시 자동) */}
      <PwaInstallPrompt />

      {/* 신규 사용자 풀 투어 (환영 모달) */}
      {showOnboarding && (
        <OnboardingTour
          availableTabs={TABS.map(t => t.id)}
          onClose={() => {
            setShowOnboarding(false);
            // V47: 모바일에선 GuidedTour 자동 시작 안 함 (tooltip 클릭 충돌 회피)
            if (!isMobile && window.confirm(storedLang() === 'zh'
              ? '要开始互动导览吗？跟着闪烁的提示在真实页面上点一点，很快就会用。'
              : '실제 화면에서 따라하는 인터랙티브 투어를 시작할까요? 깜빡이는 표시를 따라 클릭하면서 익히실 수 있어요.')) {
              setShowGuidedTour(true);
            }
          }}
          onNavigate={(tabId) => {
            setCurrentView(tabId);
            if (tabId === 'recipes') {
              setShowRecipeDetail(false);
              setSelectedId(null);
            }
          }}
        />
      )}

      {showGuidedTour && !isMobile && (
        <GuidedTour
          steps={SEMFIT_TOUR}
          availableTabs={TABS.map(t => t.id)}
          currentTab={currentView}
          onTabChange={(tabId) => {
            setCurrentView(tabId);
            if (tabId === 'recipes') {
              setShowRecipeDetail(false);
              setSelectedId(null);
            }
          }}
          onClose={() => setShowGuidedTour(false)}
        />
      )}

      {tabTutorial && (
        <TutorialModal
          tabId={tabTutorial}
          onClose={() => {
            markTutorialSeen(tabTutorial);
            setTabTutorial(null);
          }}
        />
      )}

      {editorOpen && (
        <RecipeEditor
          item={editingItem}
          copyFrom={editingItem ? null : copyingFrom}
          categories={categories || []}
          prices={prices || []}
          onSave={handleSaveRecipe}
          onDelete={handleDeleteRecipe}
          onClose={() => { setEditorOpen(false); setEditingItem(null); setCopyingFrom(null); }}
          terms={terms}
        />
      )}
    </div>
    </LangProvider>
  );
}


export default App;
